/**
 * @fileOverview This file contains the setup and management functions for a WebSocket connection using socket.io-client,
 * along with React hooks for integrating WebSocket events into a React application. It provides functionalities to
 * initiate and disconnect the socket connection, emit and listen to custom events, and a React hook to manage the
 * socket connection within React components.
 */

import { io } from "socket.io-client";
import { REACT_APP_BASE_URL, REACT_APP_SOCKET_PATH } from "./config";

let socket;

/**
 * Initiates a WebSocket connection using the provided authentication token.
 * Sets up event listeners for 'connect', 'disconnect', and 'notification' events.
 * 
 * @param {string} token - The authentication token used for establishing the WebSocket connection.
 */
export const initiateSocketConnection = (token) => {
  const url = new URL(REACT_APP_BASE_URL);
  socket = io(url.origin, {
    path: REACT_APP_SOCKET_PATH,
    auth: {
      token
    },
    transports: ["websocket"],
  });
  return socket;
};

/**
 * Disconnects the current WebSocket connection if it exists.
 */
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
  }
};

/**
 * Emits a custom event with the provided data through the WebSocket connection.
 * 
 * @param {string} eventName - The name of the event to emit.
 * @param {Object} data - The data to be sent with the event.
 */
export const emitEvent = (eventName, data) => {
  if (socket) {
    socket.emit(eventName, data);
  }
};

/**
 * Registers a callback function to be executed when a specific event is received.
 * 
 * @param {string} eventName - The name of the event to listen for.
 * @param {Function} callback - The callback function to execute when the event is received.
 */
export const onEvent = (eventName, callback) => {
  if (socket) {
    socket.on(eventName, callback);
  }
};

/**
 * Removes a previously registered event listener.
 * 
 * @param {string} eventName - The name of the event for which to remove the listener.
 * @param {Function} callback - The callback function that was registered.
 */
export const offEvent = (eventName, callback) => {
  if (socket) {
    socket.off(eventName, callback);
  }
};

/**
 * Joins a specific room through the WebSocket connection.
 * 
 * @param {string} room - The name of the room to join.
 */
export const joinRoom = (room) => {
  if (socket) socket.emit('joinRoom', room)
}

/**
 * Leaves a specific room through the WebSocket connection.
 * 
 * @param {string} room - The name of the room to leave.
 */
export const leaveRoom = (room) => {
  if (socket) socket.emit('leaveRoom', room)
}
